import { Fragment, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  List,
  Palmtree,
  Pencil,
  Plus,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatters';
import { useAuth } from '@/app/AuthProvider';
import { canManagePtoAwards } from '@/lib/permissions';
import { useProfiles } from '@/features/users/hooks';
import {
  currentPtoYear,
  useAllPtoAwards,
  useAllPtoRequests,
  usePtoRequestsForYear,
  useReviewPtoRequest,
  useUpsertPtoAward,
  type PtoAwardWithPerson,
  type PtoRequestWithPeople,
  type PtoYearRequest,
} from '@/features/pto/hooks';
import { LogPtoDialog, type LogPtoPerson } from '@/features/pto/LogPtoDialog';
import { PtoTeamCalendar } from '@/features/pto/PtoTeamCalendar';
import {
  PTO_STATUS_BADGE,
  PTO_STATUS_LABELS,
  countsAgainstPtoBalance,
  formatDays,
  formatLeaveDetail,
} from '@/features/pto/format';
import type { PtoStatus } from '@/types/database';

/**
 * "Manage PTO" — the approver / admin surface (super_admin, admin, manager).
 * All PTO (default): calendar + list of past / upcoming / pending leave.
 * Requests: approve / reject open pending requests. Employees: balances and
 * awards. Managers can Add PTO directly (no employee request needed),
 * including backdated leave. See migrations 95–97.
 */
export function ManagePtoPage() {
  const { role } = useAuth();
  const canAwards = canManagePtoAwards(role);

  const { data: profiles } = useProfiles();
  const people = useMemo<LogPtoPerson[]>(
    () =>
      (profiles ?? [])
        .filter((p) => p.is_active && p.role !== 'external_lender')
        .map((p) => ({ id: p.id, full_name: p.full_name, email: p.email })),
    [profiles],
  );

  const [logOpen, setLogOpen] = useState(false);
  const [logDefault, setLogDefault] = useState<string | null>(null);
  const openAddPto = (profileId?: string) => {
    setLogDefault(profileId ?? null);
    setLogOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Manage PTO"
        actions={
          <Button onClick={() => openAddPto()}>
            <Plus className="h-4 w-4" />
            Add PTO
          </Button>
        }
      />

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All PTO</TabsTrigger>
          <TabsTrigger value="requests">Requests</TabsTrigger>
          <TabsTrigger value="people">Employees</TabsTrigger>
        </TabsList>
        <TabsContent value="all">
          <AllPtoTab people={people} onAddPto={openAddPto} />
        </TabsContent>
        <TabsContent value="requests">
          <RequestsTab />
        </TabsContent>
        <TabsContent value="people">
          <PeopleTab canEdit={canAwards} onAddPto={openAddPto} />
        </TabsContent>
      </Tabs>

      <LogPtoDialog
        open={logOpen}
        onOpenChange={setLogOpen}
        people={people}
        defaultProfileId={logDefault}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function personInitials(name: string | null, email: string): string {
  return (name ?? email)
    .split(/[.\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');
}

function dateRange(r: Pick<PtoYearRequest, 'start_date' | 'end_date'>): string {
  return r.start_date === r.end_date
    ? formatDate(r.start_date)
    : `${formatDate(r.start_date)} – ${formatDate(r.end_date)}`;
}

// ---------------------------------------------------------------------------
// Requests tab — open / pending review queue
// ---------------------------------------------------------------------------

function RequestsTab() {
  const [status, setStatus] = useState<PtoStatus | 'all'>('pending');
  const { data: requests, isLoading } = useAllPtoRequests(status);
  const review = useReviewPtoRequest();

  const handleApprove = async (id: string) => {
    try {
      await review.mutateAsync({ id, status: 'approved' });
      toast.success('Request approved.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not approve.');
    }
  };

  const handleReject = async (id: string) => {
    const note = window.prompt('Add a note for the rejection (optional):') ?? '';
    try {
      await review.mutateAsync({
        id,
        status: 'rejected',
        review_note: note.trim() || null,
      });
      toast.success('Request rejected.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not reject.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Time-off requests</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex items-center gap-2">
          <Select
            value={status}
            onValueChange={(v) => setStatus(v as PtoStatus | 'all')}
          >
            <SelectTrigger className="sm:w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="all">All statuses</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (requests ?? []).length === 0 ? (
          <EmptyState
            icon={Palmtree}
            title="Nothing here"
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead className="text-right">Days</TableHead>
                <TableHead>Type / detail</TableHead>
                <TableHead>Status</TableHead>
                <TableHead aria-label="Actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(requests ?? []).map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        {r.requester?.avatar_url && (
                          <AvatarImage
                            src={r.requester.avatar_url}
                            alt={r.requester.email}
                          />
                        )}
                        <AvatarFallback>
                          {personInitials(
                            r.requester?.full_name ?? null,
                            r.requester?.email ?? '?',
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium text-foreground">
                        {r.requester?.full_name ??
                          r.requester?.email ??
                          'Unknown'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {dateRange(r)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatDays(r.days)}
                  </TableCell>
                  <TableCell className="max-w-[16rem] truncate text-muted-foreground">
                    {formatLeaveDetail(r)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={PTO_STATUS_BADGE[r.status]}>
                      {PTO_STATUS_LABELS[r.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {r.status === 'pending' && (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleApprove(r.id)}
                          disabled={review.isPending}
                        >
                          <Check className="h-3.5 w-3.5" />
                          Approve
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReject(r.id)}
                          disabled={review.isPending}
                        >
                          <X className="h-3.5 w-3.5" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// All PTO tab — calendar + list of past / upcoming / pending leave
// ---------------------------------------------------------------------------

type AllPtoView = 'calendar' | 'list';
type WhenFilter = 'all' | 'past' | 'upcoming' | 'pending';
type StatusFilter = 'all' | 'pending' | 'approved';

const ALL = 'all';
const MONTH_OPTIONS = [
  { value: '0', label: 'January' },
  { value: '1', label: 'February' },
  { value: '2', label: 'March' },
  { value: '3', label: 'April' },
  { value: '4', label: 'May' },
  { value: '5', label: 'June' },
  { value: '6', label: 'July' },
  { value: '7', label: 'August' },
  { value: '8', label: 'September' },
  { value: '9', label: 'October' },
  { value: '10', label: 'November' },
  { value: '11', label: 'December' },
] as const;

const VIEW_STORAGE_KEY = 'atlas:pto:allPtoView';

function todayISO(): string {
  const now = new Date();
  const m = `${now.getMonth() + 1}`.padStart(2, '0');
  const d = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

/** True when [start, end] overlaps the given calendar month (0-indexed). */
function overlapsMonth(
  start: string,
  end: string,
  year: number,
  monthIndex: number,
): boolean {
  const monthStart = `${year}-${`${monthIndex + 1}`.padStart(2, '0')}-01`;
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  const monthEnd = `${year}-${`${monthIndex + 1}`.padStart(2, '0')}-${`${lastDay}`.padStart(2, '0')}`;
  return start <= monthEnd && end >= monthStart;
}

function AllPtoTab({
  people,
  onAddPto,
}: {
  people: LogPtoPerson[];
  onAddPto: (profileId?: string) => void;
}) {
  const { data: allRequests, isLoading } = useAllPtoRequests('all');

  const [view, setView] = useState<AllPtoView>(() => {
    try {
      return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'list'
        ? 'list'
        : 'calendar';
    } catch {
      return 'calendar';
    }
  });
  const changeView = (next: AllPtoView) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // ignore
    }
  };

  const currentYear = currentPtoYear();
  const [employeeId, setEmployeeId] = useState<string>(ALL);
  const [year, setYear] = useState(String(currentYear));
  const [month, setMonth] = useState<string>(ALL);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [when, setWhen] = useState<WhenFilter>('all');

  const [calendarMonth, setCalendarMonth] = useState(
    () => new Date(currentYear, new Date().getMonth(), 1),
  );

  const yearOptions = useMemo(() => {
    const years = new Set<number>([currentYear]);
    for (const r of allRequests ?? []) {
      const y = Number(r.start_date.slice(0, 4));
      if (Number.isFinite(y)) years.add(y);
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [allRequests, currentYear]);

  const filtered = useMemo(() => {
    const today = todayISO();
    const yearNum = Number(year);
    const monthIndex = month === ALL ? null : Number(month);

    return (allRequests ?? [])
      .filter((r) => r.status === 'pending' || r.status === 'approved')
      .filter((r) => (employeeId === ALL ? true : r.profile_id === employeeId))
      .filter((r) => (status === 'all' ? true : r.status === status))
      .filter((r) => {
        if (when === 'pending') return r.status === 'pending';
        if (when === 'past') return r.end_date < today;
        if (when === 'upcoming') return r.start_date >= today;
        return true;
      })
      .filter((r) => {
        if (!Number.isFinite(yearNum)) return true;
        if (monthIndex == null) {
          // Year filter: any overlap with that calendar year.
          const yearStart = `${yearNum}-01-01`;
          const yearEnd = `${yearNum}-12-31`;
          return r.start_date <= yearEnd && r.end_date >= yearStart;
        }
        return overlapsMonth(r.start_date, r.end_date, yearNum, monthIndex);
      })
      .slice()
      .sort((a, b) => {
        if (a.start_date !== b.start_date) {
          return a.start_date < b.start_date ? 1 : -1;
        }
        return (a.requester?.full_name ?? '').localeCompare(
          b.requester?.full_name ?? '',
        );
      });
  }, [allRequests, employeeId, status, when, year, month]);

  // Keep calendar month aligned with year/month filters when they change.
  const syncCalendarFromFilters = (nextYear: string, nextMonth: string) => {
    const y = Number(nextYear);
    if (!Number.isFinite(y)) return;
    if (nextMonth === ALL) {
      setCalendarMonth(new Date(y, calendarMonth.getMonth(), 1));
    } else {
      setCalendarMonth(new Date(y, Number(nextMonth), 1));
    }
  };

  const sortedPeople = useMemo(
    () =>
      [...people].sort((a, b) =>
        (a.full_name ?? a.email).localeCompare(b.full_name ?? b.email),
      ),
    [people],
  );

  return (
    <Card>
      <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1.5">
          <CardTitle>All PTO</CardTitle>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button
            size="sm"
            onClick={() =>
              onAddPto(employeeId === ALL ? undefined : employeeId)
            }
          >
            <Plus className="h-3.5 w-3.5" />
            Add PTO
          </Button>
          <div className="flex items-center rounded-lg border border-input bg-card p-0.5">
            <button
              type="button"
              onClick={() => changeView('calendar')}
              aria-label="Calendar view"
              aria-pressed={view === 'calendar'}
              className={cn(
                'flex h-7 items-center gap-1.5 rounded-md px-2 text-xs transition-colors',
                view === 'calendar'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <CalendarDays className="h-3.5 w-3.5" />
              Calendar
            </button>
            <button
              type="button"
              onClick={() => changeView('list')}
              aria-label="List view"
              aria-pressed={view === 'list'}
              className={cn(
                'flex h-7 items-center gap-1.5 rounded-md px-2 text-xs transition-colors',
                view === 'list'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <List className="h-3.5 w-3.5" />
              List
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="sm:w-52">
              <SelectValue placeholder="Employee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All employees</SelectItem>
              {sortedPeople.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.full_name ?? p.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={year}
            onValueChange={(v) => {
              setYear(v);
              syncCalendarFromFilters(v, month);
            }}
          >
            <SelectTrigger className="w-28">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={month}
            onValueChange={(v) => {
              setMonth(v);
              syncCalendarFromFilters(year, v);
            }}
          >
            <SelectTrigger className="sm:w-40">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All months</SelectItem>
              {MONTH_OPTIONS.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={status}
            onValueChange={(v) => setStatus(v as StatusFilter)}
          >
            <SelectTrigger className="sm:w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Approved + pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>

          <Select value={when} onValueChange={(v) => setWhen(v as WhenFilter)}>
            <SelectTrigger className="sm:w-40">
              <SelectValue placeholder="When" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any time</SelectItem>
              <SelectItem value="past">Past</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="pending">Pending only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {view === 'calendar' ? (
          <PtoTeamCalendar
            requests={filtered}
            isLoading={isLoading}
            month={calendarMonth}
            onMonthChange={(next) => {
              setCalendarMonth(next);
              setYear(String(next.getFullYear()));
              // Navigating the calendar clears a specific month filter so the
              // grid stays free to move; list view still respects year.
              setMonth(ALL);
            }}
          />
        ) : (
          <AllPtoList requests={filtered} isLoading={isLoading} />
        )}
      </CardContent>
    </Card>
  );
}

function AllPtoList({
  requests,
  isLoading,
}: {
  requests: PtoRequestWithPeople[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <EmptyState
        icon={Palmtree}
        title="No time off matches"
      />
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Person</TableHead>
          <TableHead>Dates</TableHead>
          <TableHead className="text-right">Days</TableHead>
          <TableHead>Type / detail</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {requests.map((r) => (
          <TableRow key={r.id}>
            <TableCell>
              <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  {r.requester?.avatar_url && (
                    <AvatarImage
                      src={r.requester.avatar_url}
                      alt={r.requester.email}
                    />
                  )}
                  <AvatarFallback>
                    {personInitials(
                      r.requester?.full_name ?? null,
                      r.requester?.email ?? '?',
                    )}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium text-foreground">
                  {r.requester?.full_name ?? r.requester?.email ?? 'Unknown'}
                </span>
              </div>
            </TableCell>
            <TableCell className="whitespace-nowrap">{dateRange(r)}</TableCell>
            <TableCell className="text-right tabular-nums">
              {formatDays(r.days)}
            </TableCell>
            <TableCell className="max-w-[16rem] truncate text-muted-foreground">
              {formatLeaveDetail(r)}
            </TableCell>
            <TableCell>
              <Badge variant={PTO_STATUS_BADGE[r.status]}>
                {PTO_STATUS_LABELS[r.status]}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// Employees tab — balance per person + expandable dates taken
// ---------------------------------------------------------------------------

interface PersonRow {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
  award: PtoAwardWithPerson | null;
  totalDays: number;
  usedDays: number;
  remainingDays: number;
  /** Non-cancelled requests for the year, newest first. */
  requests: PtoYearRequest[];
}

function PeopleTab({
  canEdit,
  onAddPto,
}: {
  canEdit: boolean;
  onAddPto: (profileId?: string) => void;
}) {
  const year = currentPtoYear();
  const { data: profiles, isLoading: profilesLoading } = useProfiles();
  const { data: awards, isLoading: awardsLoading } = useAllPtoAwards(year);
  const { data: yearRequests, isLoading: requestsLoading } =
    usePtoRequestsForYear(year);

  const [editing, setEditing] = useState<PersonRow | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rows = useMemo<PersonRow[]>(() => {
    const awardByProfile = new Map((awards ?? []).map((a) => [a.profile_id, a]));
    const usedByProfile = new Map<string, number>();
    const requestsByProfile = new Map<string, PtoYearRequest[]>();
    for (const r of yearRequests ?? []) {
      if (r.status === 'cancelled') continue;
      const list = requestsByProfile.get(r.profile_id) ?? [];
      list.push(r);
      requestsByProfile.set(r.profile_id, list);
      if (r.status === 'approved' && countsAgainstPtoBalance(r.leave_type)) {
        usedByProfile.set(
          r.profile_id,
          (usedByProfile.get(r.profile_id) ?? 0) + Number(r.days),
        );
      }
    }
    return (profiles ?? [])
      .filter((p) => p.is_active && p.role !== 'external_lender')
      .map((p) => {
        const award = awardByProfile.get(p.id) ?? null;
        const totalDays =
          (award?.awarded_days ?? 0) + (award?.carryover_days ?? 0);
        const usedDays = usedByProfile.get(p.id) ?? 0;
        return {
          id: p.id,
          full_name: p.full_name,
          email: p.email,
          avatar_url: p.avatar_url,
          award,
          totalDays,
          usedDays,
          remainingDays: totalDays - usedDays,
          requests: requestsByProfile.get(p.id) ?? [],
        };
      });
  }, [profiles, awards, yearRequests]);

  const isLoading = profilesLoading || awardsLoading || requestsLoading;
  const colSpan = 7;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Employees — {year}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Palmtree} title="No people found" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" aria-label="Expand" />
                <TableHead>Person</TableHead>
                <TableHead className="text-right">Awarded</TableHead>
                <TableHead className="text-right">Carryover</TableHead>
                <TableHead className="text-right">Taken</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
                <TableHead aria-label="Actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const isOpen = expanded.has(row.id);
                return (
                  <Fragment key={row.id}>
                    <TableRow
                      className="cursor-pointer"
                      onClick={() => toggle(row.id)}
                    >
                      <TableCell className="text-muted-foreground">
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            {row.avatar_url && (
                              <AvatarImage src={row.avatar_url} alt={row.email} />
                            )}
                            <AvatarFallback>
                              {personInitials(row.full_name, row.email)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {row.full_name ?? row.email}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {row.requests.length} entr
                              {row.requests.length === 1 ? 'y' : 'ies'} this year
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.award ? formatDays(row.award.awarded_days) : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {row.award && row.award.carryover_days > 0
                          ? formatDays(row.award.carryover_days)
                          : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatDays(row.usedDays)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {row.award ? formatDays(row.remainingDays) : '—'}
                      </TableCell>
                      <TableCell
                        className="text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onAddPto(row.id)}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add
                          </Button>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditing(row)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              {row.award ? 'Award' : 'Set award'}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow className="bg-muted/30">
                        <TableCell />
                        <TableCell colSpan={colSpan - 1} className="py-3">
                          {row.requests.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              No PTO recorded for {year}.
                            </p>
                          ) : (
                            <ul className="space-y-1.5">
                              {row.requests.map((r) => {
                                const detail = formatLeaveDetail(r);
                                return (
                                <li
                                  key={r.id}
                                  className="flex items-center gap-3 text-sm"
                                >
                                  <Badge variant={PTO_STATUS_BADGE[r.status]}>
                                    {PTO_STATUS_LABELS[r.status]}
                                  </Badge>
                                  <span className="whitespace-nowrap font-medium text-foreground">
                                    {dateRange(r)}
                                  </span>
                                  <span className="tabular-nums text-muted-foreground">
                                    {formatDays(r.days)}
                                  </span>
                                  {detail !== '—' && (
                                    <span className="truncate text-muted-foreground">
                                      · {detail}
                                    </span>
                                  )}
                                </li>
                                );
                              })}
                            </ul>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {canEdit && (
        <AwardDialog
          person={editing}
          year={year}
          onClose={() => setEditing(null)}
        />
      )}
    </Card>
  );
}

function AwardDialog({
  person,
  year,
  onClose,
}: {
  person: PersonRow | null;
  year: number;
  onClose: () => void;
}) {
  const upsert = useUpsertPtoAward();
  const [awarded, setAwarded] = useState('');
  const [carryover, setCarryover] = useState('');
  const [note, setNote] = useState('');
  const [seededFor, setSeededFor] = useState<string | null>(null);

  // Re-seed whenever the target person changes.
  if (person && seededFor !== person.id) {
    setSeededFor(person.id);
    setAwarded(person.award ? String(person.award.awarded_days) : '');
    setCarryover(person.award ? String(person.award.carryover_days) : '0');
    setNote(person.award?.note ?? '');
  }
  if (!person && seededFor !== null) {
    setSeededFor(null);
  }

  const handleSave = async () => {
    if (!person) return;
    const awardedNum = Number(awarded);
    const carryoverNum = Number(carryover || '0');
    if (!Number.isFinite(awardedNum) || awardedNum < 0) {
      toast.error('Enter a valid number of awarded days.');
      return;
    }
    if (!Number.isFinite(carryoverNum) || carryoverNum < 0) {
      toast.error('Enter a valid number of carryover days.');
      return;
    }
    try {
      await upsert.mutateAsync({
        profile_id: person.id,
        year,
        awarded_days: awardedNum,
        carryover_days: carryoverNum,
        note: note.trim() || null,
      });
      toast.success('Award saved.');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save.');
    }
  };

  return (
    <Dialog open={!!person} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {year} PTO award — {person?.full_name ?? person?.email}
            </DialogTitle>
          </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="award-days">Awarded days</Label>
              <Input
                id="award-days"
                type="number"
                min="0"
                step="0.5"
                value={awarded}
                onChange={(e) => setAwarded(e.target.value)}
                placeholder="e.g. 15"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="award-carryover">Carryover days</Label>
              <Input
                id="award-carryover"
                type="number"
                min="0"
                step="0.5"
                value={carryover}
                onChange={(e) => setCarryover(e.target.value)}
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="award-note">Note (optional)</Label>
            <Textarea
              id="award-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Prorated — started in March"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={upsert.isPending}>
            {upsert.isPending ? 'Saving…' : 'Save award'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
